import {
  Box,
  FormControl,
  FormLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AzureOnboardingStatusStatusEnum, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { AllRegionsSelector } from '@/components/regions/allRegionsSelector';
import CopyableCode from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { SetupOwnCloudState } from './SetupOwnCloudState';
import { BulletItem } from './bulletItem';
import { extractErrorMessage } from './errorHandling';
import { FormTextField } from './formTextField';
import { OwnCloudErrorDialog } from './ownCloudErrorDialog';

export const ConnectSourceAccount = (props: StepProps<SetupOwnCloudState>) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const dal = useDAL();
  const [error, setError] = useState<Error | null>(null);
  const [tagsInput, setTagsInput] = useState(
    props.wizardState.sourceRgTags ?? '{}'
  );

  const code = `\
module "azure_source" {
  source = "https://eon.blob.core.windows.net/public/onboarding/v1.1.2/terraform/source.zip"

  management_app_id      = "${props.wizardState.managementApplicationId ?? ''}"
  backup_app_id          = "${props.wizardState.backupAppId ?? ''}"
  subscription_id        = "${props.wizardState.sourceConnectType === 'subscription' ? (props.wizardState.sourceSubscriptionId ?? '') : ''}"
  management_group_id    = "${props.wizardState.sourceConnectType === 'managementGroup' ? (props.wizardState.sourceManagementGroupId ?? '') : ''}"
  eon_backup_rg_location = "${props.wizardState.sourceRgLocation ?? 'East US'}"
  eon_backup_rg_tags     = ${props.wizardState.sourceRgTags?.trim() ? props.wizardState.sourceRgTags : '{}'}
}`;

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      const response = await dal.onboarding.sourceAccount.deploy({
        tenantId: props.wizardState.tenantId!,
        sourceSubscriptionId:
          props.wizardState.sourceConnectType === 'subscription'
            ? (props.wizardState.sourceSubscriptionId ?? '')
            : '',
        sourceManagementGroupId:
          props.wizardState.sourceConnectType === 'managementGroup'
            ? (props.wizardState.sourceManagementGroupId ?? '')
            : '',
      });

      // Check if the response has a status field and it's not succeeded
      if (
        response &&
        response.status !== AzureOnboardingStatusStatusEnum.Succeeded
      ) {
        setError(new Error('Failed to deploy source account'));
      } else {
        setIsDeploying(false);
        await props.finish();
      }
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
      setIsDeploying(false);
    }
  };

  return (
    <>
      <StepContainer
        stepperIndex={3}
        stepperLabels={props.stepperLabels}
        canGoNext={() =>
          !isDeploying && props.wizardState.sourceConnectType === 'subscription'
            ? !!props.wizardState.sourceSubscriptionId
            : !!props.wizardState.sourceManagementGroupId
        }
        nextButtonText={
          props.wizardState.onboardingFlow ? 'Connect & Continue' : 'Connect'
        }
        backButtonText={props.wizardState.onboardingFlow ? 'Back' : 'Cancel'}
        onBackClick={props.back}
        sx={{ overflow: 'auto' }}
        onNextClick={onNextClick}
      >
        <Box paddingX='min(100px,6vw)' width='100%' height='auto'>
          <BulletItem
            title='Fill in the details to generate your Terraform module, then review and deploy it in your environment.'
            leftContent={
              <Stack gap='24px'>
                {!props.wizardState.onboardingFlow && (
                  <FormTextField
                    sx={{ mb: '24px' }}
                    title='Tenant ID'
                    placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                    value={props.wizardState.tenantId || ''}
                    onChange={(event) =>
                      props.setWizardState({
                        ...props.wizardState,
                        tenantId: event.target.value,
                      })
                    }
                  />
                )}
                <FormControl>
                  <FormLabel>
                    <Typography variant='subtitle1'>
                      What do you want to connect?
                    </Typography>
                  </FormLabel>
                  <ToggleButtonGroup
                    sx={{ width: 'max-content' }}
                    exclusive
                    value={props.wizardState.sourceConnectType}
                    onChange={(_, value) =>
                      props.setWizardState({
                        ...props.wizardState,
                        sourceConnectType: value,
                      })
                    }
                  >
                    <ToggleButton value='subscription'>
                      Subscription
                    </ToggleButton>
                    <ToggleButton value='managementGroup'>
                      Management group
                    </ToggleButton>
                  </ToggleButtonGroup>
                </FormControl>
                {props.wizardState.sourceConnectType === 'subscription' && (
                  <FormTextField
                    title='Subscription ID'
                    placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                    value={props.wizardState.sourceSubscriptionId || ''}
                    onChange={(event) =>
                      props.setWizardState({
                        ...props.wizardState,
                        sourceSubscriptionId: event.target.value,
                      })
                    }
                  />
                )}
                {props.wizardState.sourceConnectType === 'managementGroup' && (
                  <FormTextField
                    title='Management group ID'
                    placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                    value={props.wizardState.sourceManagementGroupId || ''}
                    onChange={(event) =>
                      props.setWizardState({
                        ...props.wizardState,
                        sourceManagementGroupId: event.target.value,
                      })
                    }
                  />
                )}
                {props.wizardState.sourceConnectType === 'subscription' && (
                  <AllRegionsSelector
                    title="Eon's Backup resource group location"
                    provider={Provider.Azure}
                    regionName={props.wizardState.sourceRgLocation}
                    setRegionName={(regionName) =>
                      props.setWizardState({
                        ...props.wizardState,
                        sourceRgLocation: regionName,
                      })
                    }
                  />
                )}
                {props.wizardState.sourceConnectType === 'subscription' && (
                  <FormTextField
                    title="Eon's Backup resource group tags (JSON, optional)"
                    placeholder='Example: {"Environment": "Production", "Project": "Eon"}'
                    value={tagsInput}
                    onChange={(event) => {
                      const value = event.target.value;
                      setTagsInput(value);
                      props.setWizardState({
                        ...props.wizardState,
                        sourceRgTags: value,
                      });
                    }}
                  />
                )}
              </Stack>
            }
            rightContent={<CopyableCode title='main.tf' code={code} />}
          />
        </Box>
      </StepContainer>
      <OwnCloudErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
